/**
 * Membership pages — customer-facing + admin management (single file).
 *
 * `MembershipPage`  → /membership (customer)
 * `AdminMemberships` → /admin/memberships (super_admin only)
 *
 * Both consume the /api/memberships/* endpoints created in this pass. No new
 * payment provider — join is gated on `payment_reference` for paid plans.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Crown, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/features/auth/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Plan = {
    id: string;
    slug: string;
    name: string;
    tagline: string;
    price: number;
    period_days: number;
    benefits: Record<string, unknown>;
    is_active: boolean;
};

type Subscription = {
    id: string;
    plan_id: string;
    plan: Plan | null;
    status: string;
    start_date: string;
    expiry_date: string;
    payment_reference: string | null;
};

type Membership = { active: boolean; subscription: Subscription | null };

function benefitList(b: Record<string, unknown>): string[] {
    const out: string[] = [];
    if (b.free_delivery) out.push("Free delivery on every order");
    if (typeof b.delivery_discount_pct === "number" && !b.free_delivery)
        out.push(`${b.delivery_discount_pct}% off delivery`);
    if (typeof b.member_discount_pct === "number")
        out.push(`${b.member_discount_pct}% member discount`);
    if (b.priority_support) out.push("Priority support");
    if (Array.isArray(b.exclusive_coupons) && b.exclusive_coupons.length)
        out.push(`Exclusive coupons: ${b.exclusive_coupons.join(", ")}`);
    return out;
}

// ---------- Customer-facing ----------

export function MembershipPage() {
    const qc = useQueryClient();
    const { data: plans = [] } = useQuery({
        queryKey: ["memberships", "plans"],
        queryFn: async () => (await api.get<{ items: Plan[] }>("/memberships/plans")).data.items,
    });
    const { data: me } = useQuery({
        queryKey: ["memberships", "me"],
        queryFn: async () => (await api.get<Membership>("/memberships/me")).data,
    });

    const joinMut = useMutation({
        mutationFn: async (planId: string) =>
            api.post("/memberships/join", { plan_id: planId }),
        onSuccess: () => {
            toast.success("Welcome to TSGo Plus!");
            qc.invalidateQueries({ queryKey: ["memberships", "me"] });
        },
        onError: (e: unknown) => {
            // Backend returns a structured detail when the wallet is short:
            //   { message, balance, price }.  Surface it as a helpful prompt.
            const anyErr = e as {
                response?: { data?: { detail?: unknown } };
                message?: string;
            };
            const detail = anyErr.response?.data?.detail;
            if (detail && typeof detail === "object") {
                const d = detail as { balance?: number; price?: number; message?: string };
                toast.error(
                    `${d.message || "Insufficient wallet balance"} — balance ₹${
                        Math.round((d.balance ?? 0) / 100)
                    }, need ₹${Math.round((d.price ?? 0) / 100)}. Top up your wallet to continue.`,
                );
                return;
            }
            toast.error((anyErr.message as string) || "Couldn't join plan");
        },
    });

    return (
        <div data-testid="membership-page" className="mx-auto max-w-3xl px-4 py-8">
            <header className="flex items-center gap-2">
                <Crown className="h-6 w-6 text-brand" />
                <h1 className="text-3xl font-extrabold">TSGo Membership</h1>
            </header>
            <p className="mt-2 text-sm text-muted-foreground">
                Faster delivery, better prices, member-only offers.
            </p>

            {me?.active && me.subscription?.plan ? (
                <div
                    data-testid="membership-active"
                    className="mt-6 rounded-2xl border border-brand/40 bg-brand/5 p-5"
                >
                    <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-brand" />
                        <p className="font-bold">{me.subscription.plan.name} · Active</p>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                        Renews {new Date(me.subscription.expiry_date).toLocaleDateString()}
                    </p>
                    <ul className="mt-3 space-y-1 text-sm">
                        {benefitList(me.subscription.plan.benefits).map((b) => (
                            <li key={b}>· {b}</li>
                        ))}
                    </ul>
                </div>
            ) : null}

            <section className="mt-6 grid gap-4 sm:grid-cols-2">
                {plans.map((p) => {
                    const isMine = me?.subscription?.plan_id === p.id;
                    return (
                        <article
                            key={p.id}
                            data-testid={`plan-${p.slug}`}
                            className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft"
                        >
                            <div className="flex items-center gap-2">
                                <Sparkles className="h-4 w-4 text-brand" />
                                <h3 className="text-lg font-bold">{p.name}</h3>
                            </div>
                            {p.tagline ? (
                                <p className="mt-0.5 text-xs text-muted-foreground">{p.tagline}</p>
                            ) : null}
                            <p className="mt-3">
                                <span className="text-3xl font-extrabold">
                                    ₹{Math.round(p.price / 100)}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                    {" "}
                                    / {p.period_days} days
                                </span>
                            </p>
                            <ul className="mt-3 space-y-1 text-sm">
                                {benefitList(p.benefits).map((b) => (
                                    <li key={b}>· {b}</li>
                                ))}
                            </ul>
                            <button
                                type="button"
                                onClick={() => joinMut.mutate(p.id)}
                                disabled={joinMut.isPending || isMine}
                                data-testid={`plan-join-${p.slug}`}
                                className="mt-4 w-full rounded-full bg-brand py-2 text-sm font-bold text-white shadow-brand disabled:opacity-50"
                            >
                                {isMine
                                    ? "Current plan"
                                    : p.price > 0
                                      ? `Join for ₹${Math.round(p.price / 100)} (Wallet)`
                                      : "Join now"}
                            </button>
                        </article>
                    );
                })}
                {plans.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No plans available yet.</p>
                ) : null}
            </section>
        </div>
    );
}

// ---------- Admin management ----------

const EMPTY_PLAN = {
    id: "",
    name: "",
    slug: "",
    tagline: "",
    price: 0,
    period_days: 30,
    benefits: JSON.stringify(
        { free_delivery: true, member_discount_pct: 10, priority_support: true },
        null,
        2,
    ),
    is_active: true,
};

export function AdminMemberships() {
    const qc = useQueryClient();
    const [form, setForm] = useState<typeof EMPTY_PLAN>(EMPTY_PLAN);
    const editing = Boolean(form.id);
    const { data: plans = [] } = useQuery({
        queryKey: ["admin", "memberships", "plans"],
        queryFn: async () =>
            (await api.get<{ items: Plan[] }>("/admin/memberships/plans")).data.items,
    });
    const { data: subs = [] } = useQuery({
        queryKey: ["admin", "memberships", "subs"],
        queryFn: async () =>
            (await api.get<{ items: (Subscription & { customer: { email: string; full_name: string } | null })[] }>(
                "/admin/memberships/subscriptions",
            )).data.items,
    });

    const saveMut = useMutation({
        mutationFn: async () => {
            let benefits: Record<string, unknown> = {};
            try {
                benefits = JSON.parse(form.benefits || "{}");
            } catch {
                throw new Error("Benefits must be valid JSON");
            }
            const { id, ...rest } = form;
            const payload = { ...rest, benefits };
            if (id) {
                await api.put(`/admin/memberships/plans/${id}`, payload);
            } else {
                await api.post("/admin/memberships/plans", payload);
            }
        },
        onSuccess: () => {
            toast.success(editing ? "Plan updated" : "Plan created");
            setForm(EMPTY_PLAN);
            qc.invalidateQueries({ queryKey: ["admin", "memberships", "plans"] });
        },
        onError: (e: unknown) => toast.error((e as Error).message || "Save failed"),
    });

    const editPlan = (p: Plan) =>
        setForm({
            id: p.id,
            name: p.name,
            slug: p.slug,
            tagline: p.tagline,
            price: p.price,
            period_days: p.period_days,
            benefits: JSON.stringify(p.benefits, null, 2),
            is_active: p.is_active,
        });

    return (
        <div data-testid="admin-memberships" className="space-y-6">
            <header className="flex items-center gap-2">
                <Crown className="h-5 w-5 text-brand" />
                <h1 className="text-2xl font-extrabold">Memberships</h1>
            </header>

            <section className="rounded-2xl border border-border/70 bg-card p-5">
                <h3 className="text-lg font-bold">
                    {editing ? `Editing plan · ${form.name || form.slug}` : "Create plan"}
                </h3>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div>
                        <Label>Name</Label>
                        <Input
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            data-testid="admin-plan-name"
                        />
                    </div>
                    <div>
                        <Label>Slug</Label>
                        <Input
                            value={form.slug}
                            onChange={(e) => setForm({ ...form, slug: e.target.value })}
                            placeholder="tsgo-plus"
                            data-testid="admin-plan-slug"
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Tagline</Label>
                        <Input
                            value={form.tagline}
                            onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                        />
                    </div>
                    <div>
                        <Label>Price (paise) — ₹{Math.round(form.price / 100)}</Label>
                        <Input
                            type="number"
                            value={form.price}
                            onChange={(e) => setForm({ ...form, price: Number(e.target.value) })}
                            data-testid="admin-plan-price"
                        />
                    </div>
                    <div>
                        <Label>Period (days)</Label>
                        <Input
                            type="number"
                            value={form.period_days}
                            onChange={(e) => setForm({ ...form, period_days: Number(e.target.value) })}
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Benefits (JSON)</Label>
                        <textarea
                            className="min-h-[110px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                            value={form.benefits}
                            onChange={(e) => setForm({ ...form, benefits: e.target.value })}
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm sm:col-span-2">
                        <input
                            type="checkbox"
                            checked={form.is_active}
                            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                        />
                        Active
                    </label>
                </div>
                <div className="mt-3 flex gap-2">
                    <button
                        type="button"
                        onClick={() => saveMut.mutate()}
                        disabled={saveMut.isPending || !form.name || !form.slug}
                        data-testid="admin-membership-save"
                        className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
                    >
                        {editing ? "Save changes" : "Create plan"}
                    </button>
                    {editing ? (
                        <button
                            type="button"
                            onClick={() => setForm(EMPTY_PLAN)}
                            className="rounded-full border border-border/70 px-4 py-2 text-sm font-semibold"
                        >
                            Cancel
                        </button>
                    ) : null}
                </div>
            </section>

            <section className="rounded-2xl border border-border/70 bg-card p-5">
                <h3 className="text-lg font-bold">Plans</h3>
                <ul className="mt-3 space-y-2 text-sm">
                    {plans.map((p) => (
                        <li
                            key={p.id}
                            data-testid={`plan-row-${p.slug}`}
                            className="flex items-center justify-between rounded-lg bg-muted/30 px-3 py-2"
                        >
                            <span>
                                <strong>{p.name}</strong> · ₹{Math.round(p.price / 100)} /{" "}
                                {p.period_days}d
                            </span>
                            <div className="flex items-center gap-2">
                                <span className="text-[11px] font-bold uppercase text-muted-foreground">
                                    {p.is_active ? "Active" : "Inactive"}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => editPlan(p)}
                                    data-testid={`plan-edit-${p.slug}`}
                                    className="rounded-full border border-border/70 px-3 py-1 text-xs font-semibold hover:bg-muted"
                                >
                                    Edit
                                </button>
                            </div>
                        </li>
                    ))}
                    {plans.length === 0 ? (
                        <p className="text-muted-foreground">No plans yet.</p>
                    ) : null}
                </ul>
            </section>

            <section className="rounded-2xl border border-border/70 bg-card p-5">
                <h3 className="text-lg font-bold">Subscriptions</h3>
                <table className="mt-3 w-full text-sm">
                    <thead className="text-left text-xs uppercase text-muted-foreground">
                        <tr>
                            <th className="py-2">Customer</th>
                            <th>Plan</th>
                            <th>Status</th>
                            <th>Start</th>
                            <th>Expiry</th>
                        </tr>
                    </thead>
                    <tbody>
                        {subs.map((s) => (
                            <tr key={s.id} className="border-t border-border/60">
                                <td className="py-2">
                                    {s.customer?.full_name || s.customer?.email || "—"}
                                </td>
                                <td>{s.plan?.name || "—"}</td>
                                <td>
                                    <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold uppercase">
                                        {s.status}
                                    </span>
                                </td>
                                <td>{new Date(s.start_date).toLocaleDateString()}</td>
                                <td>{new Date(s.expiry_date).toLocaleDateString()}</td>
                            </tr>
                        ))}
                        {subs.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="py-4 text-center text-muted-foreground">
                                    No subscribers yet.
                                </td>
                            </tr>
                        ) : null}
                    </tbody>
                </table>
            </section>
        </div>
    );
}

export default MembershipPage;
